//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import DefaultModal from '@components/DefaultModal';
import Icon from '@components/atoms/icon/Icon';
import BlurredMedia from '@components/atoms/media/BlurredMedia';
import Media from '@components/atoms/media/Media';
import { CenteredOverflowText } from '@components/atoms/media/OverflowText';
import { getContentLibraryItemMediaType } from '@components/atoms/media/getMediaType';
import VirtualizedContentCardGrid from '@components/organisms/content-card-grid/VirtualizedContentCardGrid';
import { unmoderatedContentModalActions } from '@galleries/actions/unmoderatedContentModalActions';
import { GalleriesTrackingIds } from '@galleries/constants/galleriesConstants';
import { useRemindMeLater } from '@hooks/useRemindMeLater';
import ItemId from '@models/ItemId';
import PartialCuid from '@models/PartialCuid';
import {
  IContentLibraryExtendedItem,
  actions as streamDrawerActions,
} from '@streams/actions/streamDrawerActions';
import { FeatureStorageScope } from '@utils/featureStorageModel';
import { pluralize } from '@utils/stringUtils';

const CARD_SIZE = 122;
const NUM_CARDS_ACROSS = 5;
export const ITEMS_TO_RENDER = 10;

const TOTAL_PADDING = 16 * (NUM_CARDS_ACROSS + 1);
const TOTAL_CARD_WIDTH = CARD_SIZE * NUM_CARDS_ACROSS;
const GRID_WIDTH = TOTAL_CARD_WIDTH + TOTAL_PADDING;

// This component is specific to unmoderated Tagged by Curalate content for now, but
// can easily be expanded to include other stream's content in the future.
export const UnmoderatedContent = () => {
  const dispatch = useDispatch();

  const isOpen = useSelector(
    (state) => state.galleries.unmoderatedContentModal.isOpen,
  );
  const isAutomationModal = useSelector(
    (state) => state.streams.streamsDrawer.isAutoApprovalModalOpen,
  );
  const isAutoApprovalEntryModalOpen = useSelector(
    (state) => state.streams.streamsDrawer.isAutoApprovalEntryModalOpen,
  );

  const [, remindMeLater] = useRemindMeLater(
    FeatureStorageScope.REMIND_ME_LATER_UNMODERATED_TBC_CONTENT,
  );

  const taggedByCuralatePendingCount = useSelector(
    (state) => state.streams.streamsDrawer.taggedByCuralatePendingCount,
  ) as number;

  const items = useSelector(
    (state) => state.streams.streamsDrawer.taggedByCuralatePendingPreviewItems,
  ) as IContentLibraryExtendedItem[];

  const autoConfigTbbEnabled = useSelector(
    (state) => state.session.features.automated_tagged_by_bazaarvoice,
  ) as boolean;

  const itemsToRender = items.slice(0, ITEMS_TO_RENDER - 1);
  const itemToBlur = items[ITEMS_TO_RENDER - 1];
  const numHiddenItems = taggedByCuralatePendingCount - itemsToRender.length;

  const media = itemsToRender.map((item) => (
    <PreviewMedia
      key={item.id}
      itemType={item.itemType}
      id={ItemId(item.id)}
      imageUrl={item.imageUrl}
      metadataLayer={{
        enabled: true,
        itemDetails: item.itemDetails,
        mediaType: getContentLibraryItemMediaType(item),
      }}
      bulkSelectionLayer={{ enabled: false }}
      videoLayer={{ enabled: false }}
    />
  ));

  const blurredMedia = itemToBlur ? (
    <PreviewBlurredMedia
      imageUrl={itemToBlur.imageUrl}
      actionLayer={
        numHiddenItems > 0 ? (
          <CenteredOverflowText quantity={numHiddenItems} />
        ) : undefined
      }
    />
  ) : null;

  return (
    <DefaultModal
      closeModal={() => {
        remindMeLater(12, 'hours');
        dispatch(unmoderatedContentModalActions.closeModal());
      }}
      contents={
        <UnmoderatedContentBody>
          <VirtualizedContentCardGrid
            cardHeight={CARD_SIZE}
            cardWidth={CARD_SIZE}
            includeCardPadding={true}
            gridWidth={GRID_WIDTH}
            isRefreshingContent={false}
            orderedPartialCuids={items.map((item) => PartialCuid.build(item))}
          >
            {media}
            {blurredMedia}
          </VirtualizedContentCardGrid>
        </UnmoderatedContentBody>
      }
      isModalOpen={
        isAutomationModal || isAutoApprovalEntryModalOpen ? false : isOpen
      }
      primaryButtonInfo={{
        buttonContent: 'View Items',
        onClick: () => {
          remindMeLater(12, 'hours');
          void dispatch(streamDrawerActions.openTaggedByCuralateStream());
          dispatch(unmoderatedContentModalActions.closeModal());
        },
        trackingId: GalleriesTrackingIds.UNMODERATED_CONTENT_MODAL_VIEW_ITEMS,
      }}
      secondaryButtonInfo={{
        buttonContent: 'Remind Me Next Week',
        onClick: () => {
          // Defaults to 'days'
          remindMeLater(7);
          dispatch(unmoderatedContentModalActions.closeModal());
        },
        trackingId:
          GalleriesTrackingIds.UNMODERATED_CONTENT_MODAL_REMIND_ME_LATER,
      }}
      tertiaryButtonInfo={{
        buttonContent: (
          <AutoDistrubuteContent>
            <SettingsIcon name='settings' />
            Auto Distribute
          </AutoDistrubuteContent>
        ),
        onClick: () => {
          dispatch(unmoderatedContentModalActions.closeModal());
          void dispatch(streamDrawerActions.openCreateApprovalModal());
          void dispatch(streamDrawerActions.openTaggedByCuralateStream());
        },
        trackingId: GalleriesTrackingIds.UNMODERATED_CONTENT_MODAL_AUTO_APPROVE,
      }}
      shouldIncludeTertiaryButton={autoConfigTbbEnabled}
      title={`You have ${pluralize(taggedByCuralatePendingCount, 'item')} ready for review`}
      width='extra-wide'
    />
  );
};

const PreviewMedia = styled(Media)`
  height: ${CARD_SIZE}px;
  width: ${CARD_SIZE}px;
`;

const PreviewBlurredMedia = styled(BlurredMedia)`
  height: ${CARD_SIZE}px;
  width: ${CARD_SIZE}px;
`;

const UnmoderatedContentBody = styled.div`
  overflow-y: scroll;
  margin: auto;
  /* Override the default padding because the grid has padding built-in */
  && {
    padding-left: 0;
    padding-right: 0;
  }
`;

const SettingsIcon = styled(Icon)(
  ({ theme }) => `
  margin-right: ${theme.spacings[2]};
`,
);

const AutoDistrubuteContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;
